import { ThemeBadge, ThemeButton } from './Theme';
import { getLogLevelTone, themeToneTextClasses } from './Theme/themePresentation';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tradingApi, LogEntry, LogsResponse } from '../services/api';
import { FileText } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import { formatTimeET } from '../utils/datetime';

const DASHBOARD_LOG_FETCH_LIMIT = 2000;
const DASHBOARD_LOG_DISPLAY_LIMIT = 1000;

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>('ALL');
  const [componentFilter, setComponentFilter] = useState<string>('ALL');
  const [autoScroll, setAutoScroll] = useState(true);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logsFingerprintRef = useRef('');
  const requestInFlightRef = useRef(false);

  const fetchLogs = useCallback(async (isInitialLoad = false) => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    if (isInitialLoad) setLoading(true);
    setError(null);

    try {
      // Stagger initial API call for initial load
      if (isInitialLoad) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      const response: LogsResponse = await tradingApi.getRecentLogs(
        DASHBOARD_LOG_FETCH_LIMIT,
        levelFilter === 'ALL' ? undefined : levelFilter
      );

      if (response.success && response.data) {
        const nextLogs = response.data?.logs || [];
        const first = nextLogs[0];
        const last = nextLogs[nextLogs.length - 1];
        const fingerprint = [
          nextLogs.length,
          first?.timestamp,
          first?.message,
          last?.timestamp,
          last?.message,
        ].join('|');
        if (fingerprint !== logsFingerprintRef.current) {
          logsFingerprintRef.current = fingerprint;
          setLogs(nextLogs);
        }
      } else {
        if (isInitialLoad) setLogs([]);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
      if (isInitialLoad) setLogs([]);
    } finally {
      requestInFlightRef.current = false;
      setLoading(false);
    }
  }, [levelFilter]);

  useEffect(() => {
    fetchLogs(true);
  }, [fetchLogs]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') fetchLogs();
    };
    const intervalId = window.setInterval(refreshWhenVisible, 10000);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [fetchLogs]);

  useEffect(() => {
    // Auto-scroll to bottom when new logs arrive
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const getLevelColor = (level: string) => themeToneTextClasses[getLogLevelTone(level)];

  const getLevelBadge = (level: string) => <ThemeBadge tone={getLogLevelTone(level)}>{level}</ThemeBadge>;

  const formatTimestamp = (timestamp: string) => {
    return formatTimeET(timestamp);
  };

  const handleScroll = () => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
      setAutoScroll(isNearBottom);
    }
  };

  const levels = ['ALL', 'INFO', 'WARNING', 'ERROR'];
  const strategyComponents = ['ONStrategy', 'ORBStrategy', 'ATRDTStrategy'];

  const visibleLogs = useMemo(() => {
    const filtered = componentFilter === 'ALL'
      ? logs
      : logs.filter((log) => log.component === componentFilter);
    // Keep the dashboard DOM bounded while retaining a longer recent window.
    return filtered.slice(-DASHBOARD_LOG_DISPLAY_LIMIT);
  }, [componentFilter, logs]);

  return (
    <div className="panel full-bleed-panel w-full">
      <div className="px-6 py-5 border-b border-white/10">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-neon/10 text-neon">
              <FileText className="w-5 h-5" />
            </div>
            <div className="space-y-0.5">
              <h2 className="text-lg font-bold text-white tracking-tight">System Logs</h2>
              <p className="text-xs text-muted font-medium">Real-time telemetry, execution logs, and system alerts.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 md:justify-end">
            <div className="flex items-center gap-2 text-xs">
              {levels.map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setLevelFilter(lvl)}
                  className={`px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition ${levelFilter === lvl
                    ? 'border-neon text-neon bg-neon/10'
                    : 'border-white/15 text-muted hover:text-foreground hover:border-neon/40'
                    }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted">
              <span className="uppercase tracking-[0.16em]">Strategies</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setComponentFilter('ALL')}
                  className={`px-2 py-1 rounded-lg transition ${componentFilter === 'ALL'
                    ? 'bg-white/10 text-foreground'
                    : 'text-muted hover:text-foreground hover:bg-white/5'
                    }`}
                >
                  All
                </button>
                {strategyComponents.map((name) => (
                  <button
                    key={name}
                    onClick={() => setComponentFilter(name)}
                    className={`px-2 py-1 rounded-lg transition ${componentFilter === name
                      ? 'bg-neon/15 text-neon'
                      : 'text-muted hover:text-foreground hover:bg-white/5'
                      }`}
                  >
                    {name.replace('Strategy', '')}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted">
              <input
                type="checkbox"
                id="auto-scroll"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="h-3 w-3 text-neon focus:ring-neon border-line bg-dark-1"
              />
              Auto-scroll
            </label>
          </div>
        </div>
      </div>

      <div className="p-5">
        {error && (
          <ErrorDisplay
            error={error}
            onRetry={() => fetchLogs()}
            message="Failed to load logs"
          />
        )}

        <div className="h-80 overflow-hidden">
          <div
            ref={logsContainerRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto scrollbar-thin p-4 font-mono text-xs text-foreground"
          >
            {loading && logs.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <LoadingSpinner size="lg" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex justify-center items-center h-full text-muted">
                <div className="text-center">
                  <div className="text-4xl mb-4">📋</div>
                  <p>No logs available</p>
                </div>
              </div>
            ) : (
              <div className="space-y-0.5">
                {visibleLogs.map((log, index) => (
                  <React.Fragment key={`${log.timestamp}-${index}`}>
                    {/* Mobile layout: condensed meta, larger message */}
                    <div className="flex flex-col sm:hidden hover:bg-dark-4 p-1">
                      <div className="flex items-center justify-between w-full mb-1 text-[10px] text-muted">
                        <div className="flex items-center space-x-2">
                          <span>{formatTimestamp(log.timestamp)}</span>
                          <span className="truncate max-w-[120px]">{log.logger}</span>
                        </div>
                        <div>{getLevelBadge(log.level)}</div>
                      </div>
                      <div className="flex-1 break-words text-sm">
                        <span className={getLevelColor(log.level)}>{log.message}</span>
                      </div>
                    </div>

                    {/* Desktop layout: original column style */}
                    <div className="hidden sm:flex items-start space-x-3 hover:bg-dark-4 p-1">
                      <div className="text-[11px] text-muted mt-0.5 min-w-[80px] flex-shrink-0">
                        {formatTimestamp(log.timestamp)}
                      </div>
                      <div className="min-w-[80px] flex-shrink-0 mt-0.5">
                        {getLevelBadge(log.level)}
                      </div>
                      <div className="text-[11px] text-muted min-w-[100px] flex-shrink-0 mt-0.5">
                        {log.logger}
                      </div>
                      <div className="flex-1 break-words">
                        <span className={getLevelColor(log.level)}>{log.message}</span>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Log Stats */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted">
          <div className="flex items-center flex-wrap gap-3">
            <span>{logs.length} entries</span>
            <span>Level: {levelFilter}</span>
            <span>Auto-scroll: {autoScroll ? 'On' : 'Off'}</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeButton variant="ghost" size="sm"
              onClick={() => setAutoScroll(true)}
              disabled={autoScroll}

            >
              Go to Latest
            </ThemeButton>
            <ThemeButton variant="secondary" size="sm"
              onClick={() => fetchLogs()}

            >
              Refresh
            </ThemeButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogViewer;
